import { Spin, Tag, Alert, Collapse } from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  DollarOutlined,
} from '@ant-design/icons';

// ─── Traducciones ────────────────────────────────────────────────────────────

const VERSION_ES = {
  submitted: 'Preprint (versión enviada)',
  accepted:  'Manuscrito aceptado (AAM)',
  published: 'Versión publicada (VoR)',
};

const VERSION_TAG_COLOR = {
  submitted: 'default',
  accepted:  'processing',
  published: 'success',
};

const LOCATION_ES = {
  institutional_repository:     'Repositorio institucional',
  subject_repository:           'Repositorio temático',
  preprint_repository:          'Servidor de preprints',
  any_repository:               'Cualquier repositorio',
  any_website:                  'Cualquier sitio web',
  authors_homepage:             'Página personal del autor',
  funder_designated_location:   'Repositorio del financiador',
  named_repository:             'Repositorio designado',
  academic_social_network:      'Red social académica',
  non_commercial_repository:    'Repositorio no comercial',
  non_commercial_website:       'Web no comercial',
  non_commercial_social_network:'Red social no comercial (no comercial)',
  this_journal:                 'Esta revista',
};

const LICENSE_ES = {
  cc_by:       'CC BY',
  cc_by_nc:    'CC BY-NC',
  cc_by_nc_nd: 'CC BY-NC-ND',
  cc_by_nc_sa: 'CC BY-NC-SA',
  cc_by_nd:    'CC BY-ND',
  cc_by_sa:    'CC BY-SA',
  cc0:         'CC0 (dominio público)',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function isGreenPath(opt) {
  return (
    opt.additional_oa_fee === 'no' &&
    (opt.location?.location || []).some(l =>
      ['institutional_repository', 'subject_repository', 'any_repository',
       'non_commercial_repository', 'funder_designated_location'].includes(l)
    )
  );
}

function pathwayStyle(opt) {
  if (isGreenPath(opt)) return { border: '#52c41a', bg: '#f6ffed' };
  if (opt.additional_oa_fee === 'yes') return { border: '#1677ff', bg: '#e6f4ff' };
  return { border: '#d9d9d9', bg: '#fafafa' };
}

function locLabel(keys, named) {
  const parts = (keys || []).map(k => LOCATION_ES[k] || k);
  if (named?.length) parts.push(...named);
  return parts.join(' · ');
}

function licLabel(licenses) {
  return (licenses || [])
    .map(l => LICENSE_ES[l.license] || l.license_phrases?.[0]?.phrase || l.license)
    .join(', ');
}

// ─── Componente ──────────────────────────────────────────────────────────────

const OPFPolicyPanel = ({ loading, error, data }) => {
  if (loading) {
    return <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div>;
  }

  if (error) {
    if (error.includes('OPF_API_KEY')) {
      return (
        <Alert
          type="warning"
          showIcon
          message="API key no configurada"
          description={
            <>
              Para activar esta funcionalidad, solicita una clave gratuita en{' '}
              <a href="https://openpolicyfinder.jisc.ac.uk/help/developers/how-to-request-a-new-api-key"
                 target="_blank" rel="noopener noreferrer">
                Open Policy Finder
              </a>{' '}
              y añádela al fichero <code>.env</code> del backend como{' '}
              <code>OPF_API_KEY=tu_clave</code>.
            </>
          }
          style={{ margin: 0 }}
        />
      );
    }
    return <Alert type="error" message={error} showIcon style={{ margin: 0 }} />;
  }

  if (!data) return null;

  const items = data.items || [];
  if (items.length === 0) {
    return (
      <Alert
        type="info"
        showIcon
        message="Revista no encontrada en Open Policy Finder"
        description="Puede que esta revista no esté indexada o use un ISSN diferente."
        style={{ margin: 0 }}
      />
    );
  }

  const journal  = items[0];
  const jTitle   = journal.title?.find(t => t.preferred === 'yes')?.title
                || journal.title?.[0]?.title || '—';
  const inDoaj   = journal.listed_in_doaj === 'yes';
  const opfId    = journal.system_metadata?.id;
  const opfUrl   = opfId
    ? `https://openpolicyfinder.jisc.ac.uk/id/publication/${opfId}`
    : 'https://openpolicyfinder.jisc.ac.uk/';

  const policies   = journal.publisher_policy || [];
  const prohibited = policies.some(p => p.open_access_prohibited === 'yes');
  const allOptions = policies.flatMap(p => p.permitted_oa || []);

  if (prohibited || allOptions.length === 0) {
    return (
      <Alert
        type="warning"
        showIcon
        message="Sin opciones de autoarchivo"
        description="Esta editorial no permite depositar versiones en repositorios de acceso abierto."
        style={{ margin: 0 }}
      />
    );
  }

  return (
    <div>
      {/* Cabecera del journal */}
      <div style={{ marginBottom: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#1a1a1a', marginBottom: 4 }}>
            {jTitle}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {inDoaj && <Tag color="green">Gold OA (DOAJ)</Tag>}
            <Tag color="default" style={{ fontSize: 10 }}>
              {allOptions.length} vía{allOptions.length !== 1 ? 's' : ''} de acceso abierto
            </Tag>
          </div>
        </div>
        <a href={opfUrl} target="_blank" rel="noopener noreferrer"
          style={{ fontSize: 11, color: '#1677ff', whiteSpace: 'nowrap', flexShrink: 0 }}>
          Ver en OPF ↗
        </a>
      </div>

      {/* Una tarjeta por cada vía (permitted_oa option) */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {allOptions.map((opt, i) => {
          const { border, bg } = pathwayStyle(opt);
          const versions   = opt.article_version || [];
          const embargo    = opt.embargo;
          const locations  = opt.location?.location || [];
          const named      = opt.location?.named_repository || [];
          const licenses   = opt.license || [];
          const conditions = opt.conditions || [];
          const hasFee     = opt.additional_oa_fee === 'yes';

          return (
            <div key={i} style={{
              border: `1px solid ${border}`,
              borderLeft: `4px solid ${border}`,
              borderRadius: 6,
              background: bg,
              padding: '10px 14px',
            }}>
              {/* Versión + coste */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 8 }}>
                {versions.map(v => (
                  <Tag key={v} color={VERSION_TAG_COLOR[v] || 'default'} style={{ margin: 0 }}>
                    {VERSION_ES[v] || v}
                  </Tag>
                ))}
                <Tag
                  color={hasFee ? 'blue' : 'success'}
                  icon={hasFee ? <DollarOutlined /> : <CheckCircleOutlined />}
                  style={{ margin: 0 }}
                >
                  {hasFee ? 'Con APC' : 'Sin coste adicional'}
                </Tag>
              </div>

              {/* Embargo */}
              {embargo ? (
                <div style={{ fontSize: 12, color: '#595959', marginBottom: 5 }}>
                  <ClockCircleOutlined style={{ marginRight: 5, color: '#fa8c16' }} />
                  <strong>Embargo:</strong>{' '}
                  {embargo.amount} {embargo.units_phrases?.[0]?.phrase || embargo.units}
                </div>
              ) : (
                !hasFee && (
                  <div style={{ fontSize: 12, color: '#52c41a', marginBottom: 5 }}>
                    <CheckCircleOutlined style={{ marginRight: 5 }} />
                    <strong>Sin embargo</strong>
                  </div>
                )
              )}

              {/* Dónde depositar */}
              {(locations.length > 0 || named.length > 0) && (
                <div style={{ fontSize: 12, color: '#595959', marginBottom: 5 }}>
                  <EnvironmentOutlined style={{ marginRight: 5 }} />
                  <strong>Dónde depositar:</strong>{' '}
                  {locLabel(locations, named)}
                </div>
              )}

              {/* Licencia */}
              {licenses.length > 0 && (
                <div style={{ fontSize: 12, color: '#595959', marginBottom: 5 }}>
                  <FileTextOutlined style={{ marginRight: 5 }} />
                  <strong>Licencia:</strong> {licLabel(licenses)}
                </div>
              )}

              {/* Condiciones (colapsable) */}
              {conditions.length > 0 && (
                <Collapse
                  size="small"
                  ghost
                  style={{ marginTop: 4 }}
                  items={[{
                    key: '1',
                    label: (
                      <span style={{ fontSize: 11, color: '#888' }}>
                        Condiciones ({conditions.length})
                      </span>
                    ),
                    children: (
                      <ul style={{ margin: 0, paddingLeft: 16, fontSize: 11, color: '#666' }}>
                        {conditions.map((c, ci) => <li key={ci}>{c}</li>)}
                      </ul>
                    ),
                  }]}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Atribución */}
      <div style={{ marginTop: 14, fontSize: 10, color: '#bbb', textAlign: 'right' }}>
        Fuente: Open Policy Finder (JISC) · Licencia CC BY-NC-ND 4.0
      </div>
    </div>
  );
};

export default OPFPolicyPanel;
